import React, { useEffect, useMemo, useState } from 'react';
import {
    CCard,
    CCardBody,
    CCol,
    CRow,
    CButton,
    CBadge,
    CFormInput,
    CFormSelect,
    CAvatar,
    CTable,
    CTableHead,
    CTableRow,
    CTableHeaderCell,
    CTableBody,
    CTableDataCell,
    CDropdown,
    CDropdownToggle,
    CDropdownMenu,
    CDropdownItem,
    CSpinner
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import {
    cilPlus,
    cilSearch,
    cilOptions,
    cilPencil,
    cilTrash,
    cilBasket,
} from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import * as DomicilappService from "../../services/DomicilappService";
import { useStore } from '../../contexts/StoreContext';
import { Product } from '../../types/product';

export const ProductList: React.FC = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const { storeId } = useStore();

    useEffect(() => {
        if (storeId) {
            // 1. OBLIGATORIO: Limpiar los productos anteriores y activar cargando
            setProducts([]);
            setLoading(true);

            DomicilappService.getProductsByStore(storeId)
                .then((response) => {
                    console.log("Productos obtenidos para tienda:", storeId, response);
                    if (response?.data?.items) {
                        // 2. Reemplazar completamente el arreglo (los combos tienen su propia pantalla)
                        setProducts(response.data.items.filter((p) => !p.is_combo));
                    } else {
                        setProducts([]);
                    }
                })
                .catch((error) => {
                    console.error("Error al obtener productos:", error);
                    setProducts([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [storeId]); // Se reejecuta cada vez que storeId cambia
    // Extraer las categorías reales presentes en los productos obtenidos
    const availableCategories = useMemo(() => {
        const categoriesSet = new Set<string>();
        products.forEach((p) => {
            if (p.products_categories_name) {
                categoriesSet.add(p.products_categories_name);
            }
        });
        return Array.from(categoriesSet);
    }, [products]);

    const formatCurrency = (val: number) =>
        new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val);

    // 3. Filtrado dinámico en memoria
    const filteredProducts = products.filter((p) => {
        const matchesSearch =
            p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory =
            selectedCategory === 'all' || p.products_categories_name === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="product-list-page">
            {/* CABECERA CON ACCIÓN PRINCIPAL */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold text-dark mb-1">Catálogo de Productos</h3>
                    <p className="text-muted small mb-0">Gestiona los artículos, precios y variantes de tu tienda</p>
                </div>
                <CButton
                    color="light"
                    className="d-flex align-items-center gap-2 px-3 py-2 fw-medium border-0 shadow-sm text-body bg-body-tertiary"
                    style={{ borderRadius: "8px" }}
                    onClick={() => navigate('/products/create')}
                >
                    <CIcon icon={cilPlus} size="sm" />
                    <span>Nuevo Producto</span>
                </CButton>
            </div>

            {/* METRICAS RAPIDAS */}
            <CRow className="g-3 mb-4">
                <CCol sm={6} lg={3}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>Total Productos</span>
                            <h4 className="fw-bold text-dark mb-0 mt-1">{products.length}</h4>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={6} lg={3}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>Publicados</span>
                            <h4 className="fw-bold text-success mb-0 mt-1">{products.filter(p => p.status_id === 1).length}</h4>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={6} lg={3}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>Borradores</span>
                            <h4 className="fw-bold text-secondary mb-0 mt-1">{products.filter(p => p.status_id === 2).length}</h4>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol sm={6} lg={3}>
                    <CCard className="border-0 shadow-sm rounded-3">
                        <CCardBody className="p-3">
                            <span className="text-uppercase text-muted fw-bold" style={{ fontSize: '0.7rem' }}>Con Variantes</span>
                            <h4 className="fw-bold text-primary mb-0 mt-1">{products.filter(p => p.has_variants === 1 || p.has_variants === 1).length}</h4>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>

            {/* CARD CON FILTROS Y TABLA */}
            <CCard className="border-0 shadow-sm rounded-3">
                <CCardBody className="p-4">
                    <CRow className="g-3 mb-4">
                        <CCol md={6}>
                            <div className="position-relative">
                                <CFormInput
                                    placeholder="Buscar por nombre o descripción..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                />
                                <CIcon
                                    icon={cilSearch}
                                    className="position-absolute text-muted"
                                    style={{ left: '14px', top: '50%', transform: 'translateY(-50%)' }}
                                />
                            </div>
                        </CCol>
                        <CCol md={4}>
                            {/* <CFormSelect
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="all">Todas las Categorías</option>
                                <option value="Pizzas Especiales">Pizzas Especiales</option>
                                <option value="Hamburguesas">Hamburguesas</option>
                                <option value="Bebidas">Bebidas</option>
                            </CFormSelect> */}
                            <CFormSelect
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="all">Todas las Categorías</option>
                                {availableCategories.map((cat, idx) => (
                                    <option key={idx} value={cat}>
                                        {cat}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CCol>
                    </CRow>

                    {/* TABLA PRINCIPAL */}
                    <CTable responsive align="middle" hover className="border-top">
                        <CTableHead>
                            <CTableRow>
                                <CTableHeaderCell style={{ width: '60px' }}>Imagen</CTableHeaderCell>
                                <CTableHeaderCell>Producto</CTableHeaderCell>
                                <CTableHeaderCell>Categoría</CTableHeaderCell>
                                <CTableHeaderCell>Estado</CTableHeaderCell>
                                <CTableHeaderCell>Estructura</CTableHeaderCell>
                                <CTableHeaderCell>Precio / Rango</CTableHeaderCell>
                                <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
                            </CTableRow>
                        </CTableHead>

                        <CTableBody>
                            {loading ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={7} className="text-center py-5">
                                        <CSpinner color="primary" size="sm" className="me-2" />
                                        <span className="text-muted">Cargando productos de la tienda...</span>
                                    </CTableDataCell>
                                </CTableRow>
                            ) : filteredProducts.length === 0 ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={7} className="text-center py-5 text-muted">
                                        No se encontraron productos registrados para esta tienda.
                                    </CTableDataCell>
                                </CTableRow>
                            ) : (
                                filteredProducts.map((p) => (
                                    <CTableRow key={p.id}>
                                        <CTableDataCell>
                                            {p.variants?.length > 0 && p.variants[0].image ? (
                                                <CAvatar src={p.variants[0].image} shape="rounded" size="md" />
                                            ) : p.image ? (
                                                <CAvatar src={p.image} shape="rounded" size="md" />
                                            ) : (
                                                <div className="rounded d-flex align-items-center justify-content-center text-muted bg-light" style={{ width: '40px', height: '40px' }}>
                                                    <CIcon icon={cilBasket} />
                                                </div>
                                            )}
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <div className="fw-bold text-dark">{p.name}</div>
                                            <small className="text-muted d-block text-truncate" style={{ maxWidth: '250px' }}>
                                                {p.description}
                                            </small>
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <CBadge className="text-dark border bg-light">
                                                {p.products_categories_name || 'Sin Categoría'}
                                            </CBadge>
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            {p.status_id === 1 ? (
                                                <CBadge color="success-subtle" className="text-success border border-success-subtle px-2 py-1">
                                                    ● Publicado
                                                </CBadge>
                                            ) : (
                                                <CBadge color="secondary-subtle" className="text-secondary border px-2 py-1">
                                                    ● Borrador
                                                </CBadge>
                                            )}
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            {p.has_variants ? (
                                                <CBadge color="primary-subtle" className="text-primary fw-semibold">
                                                    {p.variants?.length} Variantes
                                                </CBadge>
                                            ) : (
                                                <small className="text-muted">Producto Simple</small>
                                            )}
                                        </CTableDataCell>
                                        <CTableDataCell className="fw-bold text-dark">
                                            {p.has_variants && p.variants && p.variants.length > 0 ? (
                                                <span>
                                                    {formatCurrency(Math.min(...p.variants.map((v) => v.price)))} -{' '}
                                                    {formatCurrency(Math.max(...p.variants.map((v) => v.price)))}
                                                </span>
                                            ) : (
                                                formatCurrency(p.price || 0)
                                            )}
                                        </CTableDataCell>
                                        <CTableDataCell className="text-end">
                                            <CDropdown>
                                                <CDropdownToggle
                                                    color="transparent"
                                                    caret={false}
                                                    className="btn-icon p-1 text-body-secondary border-0"
                                                >
                                                    <CIcon icon={cilOptions} />
                                                </CDropdownToggle>
                                                <CDropdownMenu className="shadow-lg border-0 bg-body-tertiary p-1" style={{ minWidth: "160px" }}>
                                                    <CDropdownItem
                                                        className="d-flex align-items-center gap-2 rounded px-3 py-2 text-body small cursor-pointer"
                                                        onClick={() => navigate(`/products/edit/${p.id}`)}
                                                    >
                                                        <CIcon icon={cilPencil} size="sm" className="text-body-secondary" />
                                                        <span>Editar</span>
                                                    </CDropdownItem>
                                                    <CDropdownItem className="d-flex align-items-center gap-2 rounded px-3 py-2 text-danger small cursor-pointer">
                                                        <CIcon icon={cilTrash} size="sm" />
                                                        <span>Eliminar</span>
                                                    </CDropdownItem>
                                                </CDropdownMenu>
                                            </CDropdown>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))
                            )}
                        </CTableBody>
                    </CTable>
                </CCardBody>
            </CCard>
        </div>
    );
};

export default ProductList;